import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import TeamSelector from '../components/TeamSelector';
import ConfirmDialog from '../components/ConfirmDialog';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CurrencyDollarIcon,
  DocumentIcon,
} from '@heroicons/react/24/outline';

const Budget = () => {
  const { user, isCoach } = useAuth();
  
  const [confirmState, setConfirmState] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teams, setTeams] = useState([]);
  const [budgets, setBudgets] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  
  const [formData, setFormData] = useState({
    teamId: '',
    totalBudget: '',
    spentAmount: '',
    notes: ''
  });

  // Demo teams data
  const demoTeams = [
    { id: 1, name: 'Demo Braves', sport: 'baseball', ageGroup: 'U12' },
    { id: 2, name: 'Demo Padres', sport: 'baseball', ageGroup: 'U14' },
    { id: 3, name: 'Demo Angels', sport: 'baseball', ageGroup: 'U16' }
  ];

  // Demo budget data
  const demoBudgets = {
    1: { totalBudget: 5000, spentAmount: 1200, notes: 'Tournament fees and equipment' },
    2: { totalBudget: 7500, spentAmount: 2100, notes: 'Travel expenses and uniforms' },
    3: { totalBudget: 6000, spentAmount: 800, notes: 'Field rental and coaching fees' }
  };

  useEffect(() => {
    // Simulate loading and set demo data
    setTimeout(() => {
      setTeams(demoTeams);
      setBudgets(demoBudgets);
      setLoading(false);
    }, 500);
  }, []);

  const handleTeamChange = (team) => {
    setSelectedTeam(team);
  };

  const handleInputChange = (e) => {
    const { name, value } = setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddBudget = (e) => {
    e.preventDefault();
    
    const newBudget = {
      totalBudget: parseFloat(formData.totalBudget) || 0,
      spentAmount: parseFloat(formData.spentAmount) || 0,
      notes: formData.notes
    };

    setBudgets(prev => ({
      ...prev,
      [formData.teamId]: newBudget
    }));

    setFormData({ teamId: '', totalBudget: '', spentAmount: '', notes: '' });
    setShowAddForm(false);
  };

  const handleEditBudget = (teamId, budget) => {
    setEditingBudget({ teamId, ...budget });
    setFormData({
      teamId: teamId.toString(),
      totalBudget: budget.totalBudget.toString(),
      spentAmount: budget.spentAmount.toString(),
      notes: budget.notes
    });
    setShowAddForm(true);
  };

  const handleUpdateBudget = (e) => {
    e.preventDefault();
    
    const updatedBudget = {
      totalBudget: parseFloat(formData.totalBudget) || 0,
      spentAmount: parseFloat(formData.spentAmount) || 0,
      notes: formData.notes
    };

    setBudgets(prev => ({
      ...prev,
      [editingBudget.teamId]: updatedBudget
    }));

    setFormData({ teamId: '', totalBudget: '', spentAmount: '', notes: '' });
    setShowAddForm(false);
    setEditingBudget(null);
  };

  const handleDeleteBudget = (teamId) => {
    setConfirmState({
      title: 'Delete Budget',
      message: 'Are you sure you want to delete this budget?',
      isDangerous: true,
      onConfirm: () => {
        setBudgets(prev => {
          const newBudgets = { ...prev };
          delete newBudgets[teamId];
          return newBudgets;
        });
      },
    });
  };

  const getRemainingBudget = (budget) => {
    return (budget.totalBudget || 0) - (budget.spentAmount || 0);
  };

  const getProgressPercentage = (budget) => {
    if (!budget.totalBudget) return 0;
    return Math.round(((budget.spentAmount || 0) / budget.totalBudget) * 100);
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return 'bg-red-500';
    if (percentage >= 60) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <CurrencyDollarIcon className="h-8 w-8 text-green-600 mr-3" />
              Team Budgets
            </h1>
            <p className="text-gray-600 mt-1">
              Manage budgets for your teams
            </p>
          </div>
          {isCoach && (
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Budget
            </button>
          )}
        </div>
      </div>

      {/* Budget Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {teams.map((team) => {
          const budget = budgets[team.id];
          const remaining = getRemainingBudget(budget);
          const progressPercentage = getProgressPercentage(budget);
          
          return (
            <div key={team.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              {/* Team Header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{team.name}</h3>
                  <p className="text-sm text-gray-500">{team.sport} • {team.ageGroup}</p>
                </div>
                {isCoach && budget && (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditBudget(team.id, budget)}
                      className="p-2 text-gray-400 hover:text-blue-600"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteBudget(team.id)}
                      className="p-2 text-gray-400 hover:text-red-600"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Budget Info */}
              {budget ? (
                <div className="space-y-4">
                  {/* Budget Amounts */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Total Budget</p>
                      <p className="text-lg font-semibold text-gray-900">
                        ${budget.totalBudget.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Spent</p>
                      <p className="text-lg font-semibold text-red-600">
                        ${budget.spentAmount.toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {/* Remaining */}
                  <div>
                    <p className="text-sm text-gray-500">Remaining</p>
                    <p className={`text-lg font-semibold ${remaining >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ${remaining.toLocaleString()}
                    </p>
                  </div>

                  {/* Progress Bar */}
                  <div>
                    <div className="flex justify-between text-sm text-gray-500 mb-1">
                      <span>Progress</span>
                      <span>{progressPercentage}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(progressPercentage)}`}
                        style={{ width: `${Math.min(progressPercentage, 100)}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Notes */}
                  {budget.notes && (
                    <div>
                      <p className="text-sm text-gray-500">Notes</p>
                      <p className="text-sm text-gray-700">{budget.notes}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <DocumentIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-500 mb-3">No budget set</p>
                  {isCoach && (
                    <button
                      onClick={() => {
                        setFormData(prev => ({ ...prev, teamId: team.id.toString() }));
                        setShowAddForm(true);
                      }}
                      className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                      Add Budget
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add/Edit Budget Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingBudget ? 'Edit Budget' : 'Add Budget'}
              </h3>
              <form onSubmit={editingBudget ? handleUpdateBudget : handleAddBudget} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Team
                  </label>
                  <select
                    name="teamId"
                    value={formData.teamId}
                    onChange={(e) => setFormData(prev => ({ ...prev, teamId: e.target.value }))}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={!!editingBudget}
                  >
                    <option value="">Select a team</option>
                    {teams.map(team => (
                      <option key={team.id} value={team.id}>
                        {team.name} ({team.sport} • {team.ageGroup})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total Budget ($)
                  </label>
                  <input
                    type="number"
                    name="totalBudget"
                    value={formData.totalBudget}
                    onChange={(e) => setFormData(prev => ({ ...prev, totalBudget: e.target.value }))}
                    required
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount Spent ($)
                  </label>
                  <input
                    type="number"
                    name="spentAmount"
                    value={formData.spentAmount}
                    onChange={(e) => setFormData(prev => ({ ...prev, spentAmount: e.target.value }))}
                    required
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Optional notes about the budget..."
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false);
                      setEditingBudget(null);
                      setFormData({ teamId: '', totalBudget: '', spentAmount: '', notes: '' });
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                  >
                    {editingBudget ? 'Update Budget' : 'Add Budget'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!confirmState}
        title={confirmState?.title}
        message={confirmState?.message}
        isDangerous={confirmState?.isDangerous}
        onConfirm={() => { confirmState.onConfirm(); setConfirmState(null); }}
        onCancel={() => setConfirmState(null)}
      />
    </div>
  );
};

export default Budget;